import styled from 'styled-components';
import { spacer32, Type, spacer8 } from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { CopyToGreenCheckAnimation } from '@components/shared/CopyToGreenCheckAnimation';
import { PillInfoWithIcon } from '@components/shared/PillInfo/PillInfoWithIcon';
import { TextLinkToExternal } from '@components/shared/TextLinkToExternal';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';

const MetadataContainer = styled.div`
  display: flex;
  margin-bottom: 80px;
`;

const Column = styled.div`
  flex: 0.5;
`;

const CopyIconContainer = styled.div`
  margin-right: ${spacer8};
`;

interface MetadataProps {
  uri: string;
  url: string;
}

export const Metadata = ({ uri, url }: MetadataProps) => {
  const t = useT();

  const COPY_URI = t(
    'atlas.actions.copy_uri',
    `Copy URI`,
    'Text for Copy URI action',
  );
  const COPY_URL = t(
    'atlas.actions.copy_url',
    `Copy URL`,
    'Text for Copy URL action',
  );
  const COPIED_TO_CLIPBOARD = t(
    'atlas.snackbars.copied_to_clipboard',
    'Copied to clipboard',
    'Snackbar message to indicate copied to clipboard',
  );

  return (
    <MetadataContainer>
      <Column>
        <Type as="h3" variant="celloCanon" paddingBottom={spacer32}>
          {t(
            'atlas.artist_metadata',
            'Artist metadata',
            'Title for Artist metadata section',
          )}
        </Type>
        <PillInfoWithIcon
          label="URI"
          value={
            <TextLinkToExternal
              href={uri}
              valueText={uri}
              valueTextColor="textBrightAccent"
              hasTextUnderline={false}
              handleClick={undefined}
            />
          }
          icon={
            <CopyIconContainer>
              <CopyToGreenCheckAnimation
                hasHoverBackground
                label="Copy URI"
                tooltipText={COPY_URI}
                copyText={uri}
                snackbarText={COPIED_TO_CLIPBOARD}
                screenReaderText={COPY_URI}
                handleClick={() =>
                  sendEvent({
                    eventCategory: EVENT_CATEGORY.ARTIST_METADATA_SECTION,
                    eventAction: EVENT_ACTION.CLICK,
                    eventLabel: 'URI copied',
                    product: GA_PRODUCT_CATALOG,
                  })
                }
              />
            </CopyIconContainer>
          }
        />
        <PillInfoWithIcon
          label="URL"
          value={
            <TextLinkToExternal
              href={url}
              valueText={url}
              valueTextColor="textBrightAccent"
              hasTextUnderline={false}
              handleClick={undefined}
            />
          }
          icon={
            <CopyIconContainer>
              <CopyToGreenCheckAnimation
                hasHoverBackground
                label="Copy URL"
                tooltipText={COPY_URL}
                copyText={url}
                snackbarText={COPIED_TO_CLIPBOARD}
                screenReaderText={COPY_URL}
                handleClick={() =>
                  sendEvent({
                    eventCategory: EVENT_CATEGORY.ARTIST_METADATA_SECTION,
                    eventAction: EVENT_ACTION.CLICK,
                    eventLabel: 'URL copied',
                    product: GA_PRODUCT_CATALOG,
                  })
                }
              />
            </CopyIconContainer>
          }
        />
      </Column>
    </MetadataContainer>
  );
};
